import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { useVisibility } from '../hooks/useVisibility.js';
import { useCompetitorScores } from '../hooks/useCompetitorScores.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { ScoreBadge } from '../components/ScoreBadge.js';
import { EngineVisibilityCard, aggregateByEngine, reliabilityTierForSampleSize, ENGINE_ORDER } from '../components/EngineVisibilityCard.js';
import { PausedEngineCard } from '../components/PausedEngineCard.js';
import { RegionVisibilitySplit } from '../components/RegionVisibilitySplit.js';
import { RegionSourceOverlap } from '../components/RegionSourceOverlap.js';
import { useRegionSourceOverlap } from '../hooks/useRegionSourceOverlap.js';
import { ShareOfVoiceBar } from '../components/ShareOfVoiceBar.js';
import { RankingsTable } from '../components/RankingsTable.js';
import { GapSummary } from '../components/GapSummary.js';
import { DateRangeSelector } from '../components/DateRangeSelector.js';
import { BrandReportButton } from '../components/BrandReportButton.js';
import { TenantOverview } from '../components/TenantOverview.js';
import { EngineBadge } from '../components/EngineBadge.js';
import { PipelineSummaryBar } from '../components/PipelineSummaryBar.js';
import { EngineStrip } from '../components/EngineStrip.js';
import { StageLoop } from '../components/how-it-works/diagrams/LoopDiagram.js';
import { VisibilityChart, MultiBrandVisibilityChart } from '../components/VisibilityChart.js';
import type { BrandSeries } from '../components/VisibilityChart.js';
import type { DateRange } from '../components/DateRangeSelector.js';
import type { ResponseListItem, ResponseListResponse, PipelineStatus, ReliabilityTier } from '../types/index.js';
import type { SovSegment } from '../components/ShareOfVoiceBar.js';
import type { RankedBrand } from '../hooks/useCompetitorScores.js';
import type { EngineAggregation } from '../components/EngineVisibilityCard.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Region, RegionHead } from '../components/ui/Region.js';
import { Segmented } from '../components/ui/Segmented.js';
import { StatRow, Stat } from '../components/ui/StatRow.js';
import { MeasureBar } from '../components/ui/MeasureBar.js';
import { SearchConsolePanel } from '../components/SearchConsolePanel.js';

const CHART_MODES = [
  { value: 'engine', label: 'By engine' },
  { value: 'brand', label: 'By brand' },
] as const;

export function OverviewPage() {
  const { selectedBrand } = useBrand();
  const [dateRange, setDateRange] = useState<DateRange>('30d');

  // When no brand is selected, show tenant-level stats
  if (!selectedBrand) {
    return (
      <div data-testid="overview-page" className="hub-page--dashboard" data-density="compact">
        <Region>
          <h1 className="hub-page__title">Overview</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">Tenant-level overview across all brands</p>
        </Region>
        <Region>
          <TenantOverview />
        </Region>
      </div>
    );
  }

  // Brand is selected — show brand visibility data
  return (
    <BrandOverview
      tenantId={selectedBrand.tenant_id}
      brandId={selectedBrand.id}
      brandName={selectedBrand.name}
      brandIndustry={selectedBrand.industry ?? undefined}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
    />
  );
}

interface BrandOverviewProps {
  tenantId: string | null;
  brandId: string;
  brandName: string;
  brandIndustry?: string;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/** Compute KPI values from engine aggregations and competitor rankings. */
function useKpiValues(
  engineAggs: EngineAggregation[],
  rankings: RankedBrand[],
  brandName: string,
) {
  return useMemo(() => {
    // Quality Score: weighted average of mention_rate across engines (scaled to 10)
    const mentionRates = engineAggs.map((e) => e.mention_rate).filter((v) => v != null);
    const avgMentionRate = mentionRates.length > 0
      ? mentionRates.reduce((a, b) => a + b, 0) / mentionRates.length
      : 0;
    const qualityScore = (avgMentionRate * 10).toFixed(1);

    // Mention Rate: average across engines
    const mentionRatePct = Math.round(avgMentionRate * 100);

    // Avg Sentiment
    const sentiments = engineAggs
      .map((e) => e.avg_sentiment)
      .filter((v): v is number => v != null);
    const avgSentiment = sentiments.length > 0
      ? sentiments.reduce((a, b) => a + b, 0) / sentiments.length
      : 0;

    // Share of Voice — sample-weighted average of per-engine SOV from
    // visibility_scores. The earlier formula (selectedBrand / Σ registered
    // competitors) was misleading for brands with no tracked competitors
    // (denominator collapsed to the brand itself → 100% trivially). The
    // per-engine SOV is sourced from competitors actually mentioned in each
    // response, which is the canonical measure across the page.
    let sovWeightedSum = 0;
    let sovTotalSamples = 0;
    for (const e of engineAggs) {
      if (e.share_of_voice != null && e.sample_size > 0) {
        sovWeightedSum += e.share_of_voice * e.sample_size;
        sovTotalSamples += e.sample_size;
      }
    }
    const brandSov = sovTotalSamples > 0 ? sovWeightedSum / sovTotalSamples : 0;

    // Top competitor still uses rankings (separate hook fetches each
    // registered competitor's visibility) — kept as the subtitle, but it
    // requires at least one tracked competitor with measurable visibility.
    let topCompetitor: { name: string; sov: number } | null = null;
    for (const rank of rankings) {
      if (rank.isSelected) continue;
      let compW = 0;
      let compS = 0;
      for (const score of rank.scores) {
        if (score.share_of_voice != null && score.sample_size > 0) {
          compW += score.share_of_voice * score.sample_size;
          compS += score.sample_size;
        }
      }
      const compSov = compS > 0 ? compW / compS : 0;
      if (compSov > 0 && (!topCompetitor || compSov > topCompetitor.sov)) {
        topCompetitor = { name: rank.name, sov: compSov };
      }
    }

    const sovSubtitle = topCompetitor
      ? `vs ${topCompetitor.name} ${Math.round(topCompetitor.sov * 100)}%`
      : undefined;

    // Unprompted mention rate — sample-weighted across engines so a single
    // engine with one unprompted query doesn't dominate. Null when no
    // unprompted samples exist (every prompt explicitly named the brand).
    let unpromptedMentions = 0;
    let unpromptedSamples = 0;
    for (const e of engineAggs) {
      if (e.unprompted_mention_rate != null && e.unprompted_sample_size > 0) {
        unpromptedMentions += e.unprompted_mention_rate * e.unprompted_sample_size;
        unpromptedSamples += e.unprompted_sample_size;
      }
    }
    const unpromptedMentionRate = unpromptedSamples > 0
      ? unpromptedMentions / unpromptedSamples
      : null;
    const unpromptedMentionRatePct = unpromptedMentionRate != null
      ? Math.round(unpromptedMentionRate * 100)
      : null;

    return {
      qualityScore,
      mentionRatePct,
      unpromptedMentionRatePct,
      unpromptedSampleSize: unpromptedSamples,
      avgSentiment,
      brandSov,
      sovSubtitle,
    };
  }, [engineAggs, rankings, brandName]);
}

/** Build SOV segments from competitor rankings — relative shares summing to ~100%. */
function useSovSegments(rankings: RankedBrand[]): SovSegment[] {
  return useMemo(() => {
    // Collect raw mention counts per brand across all engines
    const brandMentions: { name: string; isSelected: boolean; mentions: number }[] = [];
    for (const rank of rankings) {
      // Use mention_rate * sample_size as a proxy for mention count
      let totalMentions = 0;
      for (const score of rank.scores) {
        const rate = score.mention_rate ?? 0;
        const samples = score.sample_size ?? 0;
        totalMentions += rate * samples;
      }
      if (totalMentions > 0 || rank.isSelected) {
        brandMentions.push({
          name: rank.name,
          isSelected: rank.isSelected,
          mentions: totalMentions,
        });
      }
    }

    const totalAllMentions = brandMentions.reduce((sum, b) => sum + b.mentions, 0);
    if (totalAllMentions === 0) return [];

    // Compute relative share: brand_mentions / total_mentions
    return brandMentions.map((b) => ({
      name: b.name,
      share: b.mentions / totalAllMentions,
      isSelected: b.isSelected,
    }));
  }, [rankings]);
}

/** Generate a human-readable narrative summary from KPI data. */
function generateNarrativeSummary(
  brandName: string,
  engineAggs: EngineAggregation[],
  kpis: { qualityScore: string; mentionRatePct: number; unpromptedMentionRatePct: number | null; brandSov: number; sovSubtitle?: string },
  gapCount: number,
  headlineTier: ReliabilityTier,
  totalSamples: number,
): string {
  const engineCount = engineAggs.length;
  // For "best engine" we now prefer the unprompted rate when available — that
  // measures organic surface, not the LLM echoing back a branded prompt.
  const useUnprompted = engineAggs.some((e) => e.unprompted_mention_rate != null);
  const engineRate = (e: EngineAggregation) =>
    useUnprompted ? (e.unprompted_mention_rate ?? -1) : e.mention_rate;
  const mentionedEngines = engineAggs.filter((e) => engineRate(e) > 0);
  const zeroEngines = engineAggs.filter((e) => engineRate(e) === 0);

  const headlinePct = kpis.unpromptedMentionRatePct ?? kpis.mentionRatePct;
  const headlineLabel = kpis.unpromptedMentionRatePct != null ? 'unprompted mention rate' : 'overall mention rate';
  let summary = `${brandName} has ${headlinePct}% ${headlineLabel} across ${engineCount} AI engine${engineCount !== 1 ? 's' : ''}.`;

  if (mentionedEngines.length > 0) {
    const best = [...mentionedEngines].sort((a, b) => engineRate(b) - engineRate(a))[0];
    const bestLabel = ENGINE_LABELS_MAP[best.engine] ?? best.engine;
    const bestPct = Math.round(engineRate(best) * 100);
    summary += ` ${bestLabel} provides the strongest visibility (${bestPct}% mentioned)`;

    if (zeroEngines.length > 0) {
      const zeroNames = zeroEngines.map((e) => ENGINE_LABELS_MAP[e.engine] ?? e.engine);
      summary += `, while ${zeroNames.join(', ')} show${zeroEngines.length === 1 ? 's' : ''} 0% mentions`;
    }
    summary += '.';
  }

  if (gapCount > 0) {
    summary += ` ${gapCount} gap${gapCount !== 1 ? 's' : ''} detected requiring attention.`;
  }

  if (kpis.brandSov > 0) {
    summary += ` Share of voice stands at ${Math.round(kpis.brandSov * 100)}%${kpis.sovSubtitle ? ` (${kpis.sovSubtitle})` : ''}.`;
  }

  // Thin-sample honesty caveat — only when the worst contributing tier is
  // directional. Plain language, no metric thresholds quoted to the end user.
  if (headlineTier === 'directional') {
    summary += ` (directional — based on a small sample of ${totalSamples} response${totalSamples !== 1 ? 's' : ''}.)`;
  }

  return summary;
}

const ENGINE_LABELS_MAP: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  google_aio: 'Google AIO',
};

function BrandOverview({
  tenantId,
  brandId,
  brandName,
  brandIndustry,
  dateRange,
  onDateRangeChange,
}: BrandOverviewProps) {
  const { latest, history, regions, loading: visLoading, error: visError } = useVisibility(tenantId, brandId, dateRange);
  const { rankings, loading: compLoading, error: compError } = useCompetitorScores(tenantId, brandId, brandName);

  // Spec 171: the region split + region chrome ONLY appear when a brand has ≥2
  // regions of data. Single-region/no-region brands fall through to the existing
  // flat "Visibility by engine" section, byte-identical to pre-171.
  const hasMultiRegion = regions.length >= 2;
  const { overlap: regionOverlap } = useRegionSourceOverlap(
    hasMultiRegion ? tenantId : null,
    hasMultiRegion ? brandId : null,
  );

  // Pipeline status
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [pipelineFailed, setPipelineFailed] = useState(false);

  const fetchPipelineStatus = useCallback(async () => {
    if (!tenantId || !brandId) return;
    try {
      const data = await apiGet<PipelineStatus>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/pipeline/status`,
      );
      setPipelineStatus(data);
      setPipelineFailed(false);
    } catch {
      // The engine strip says so; the rest of the page does not depend on it.
      setPipelineFailed(true);
    }
  }, [tenantId, brandId]);

  useEffect(() => { fetchPipelineStatus(); }, [fetchPipelineStatus]);

  const handleRecomputeScores = async () => {
    if (!tenantId || !brandId) return;
    await apiFetch(`/api/v1/tenants/${tenantId}/brands/${brandId}/visibility/compute`, { method: 'POST' });
    fetchPipelineStatus();
  };

  const handleRedetectGaps = async () => {
    if (!tenantId || !brandId) return;
    await apiFetch(`/api/v1/tenants/${tenantId}/brands/${brandId}/gaps/detect`, { method: 'POST' });
    fetchPipelineStatus();
  };

  const handleRecomputeDomainScores = async () => {
    if (!tenantId || !brandId) return;
    await apiFetch(`/api/v1/tenants/${tenantId}/brands/${brandId}/sources/scores/compute`, { method: 'POST' });
    fetchPipelineStatus();
  };

  // Gap count for narrative summary
  const [gapCount, setGapCount] = useState(0);

  const fetchGapCount = useCallback(async () => {
    if (!tenantId || !brandId) return;
    try {
      const gapData = await apiGet<{ data: unknown[]; total: number }>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/gaps?limit=1`,
      );
      setGapCount(gapData.total);
    } catch {
      // Non-critical
    }
  }, [tenantId, brandId]);

  useEffect(() => { fetchGapCount(); }, [fetchGapCount]);

  // Competitive visibility history (Spec 062)
  const [competitiveHistory, setCompetitiveHistory] = useState<BrandSeries[]>([]);
  const [chartMode, setChartMode] = useState<'engine' | 'brand'>('engine');

  const fetchCompetitiveHistory = useCallback(async () => {
    if (!tenantId || !brandId) return;
    try {
      const today = new Date();
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateTo = today.toISOString().slice(0, 10);
      const dateFrom = thirtyDaysAgo.toISOString().slice(0, 10);
      const result = await apiGet<{ data: BrandSeries[] }>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/visibility/competitive-history?date_from=${dateFrom}&date_to=${dateTo}&granularity=daily`,
      );
      setCompetitiveHistory(result.data);
    } catch {
      // Non-critical
    }
  }, [tenantId, brandId]);

  useEffect(() => { fetchCompetitiveHistory(); }, [fetchCompetitiveHistory]);

  // Recent responses
  const [recentResponses, setRecentResponses] = useState<ResponseListItem[]>([]);

  const fetchRecentResponses = useCallback(async () => {
    if (!tenantId || !brandId) return;
    try {
      const respData = await apiGet<ResponseListResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/responses?limit=6&sort=captured_at&order=desc`,
      );
      setRecentResponses(respData.data);
    } catch {
      // Non-critical
    }
  }, [tenantId, brandId]);

  useEffect(() => { fetchRecentResponses(); }, [fetchRecentResponses]);

  // Competitor mentions (replaces Share of Voice)
  const [competitorMentions, setCompetitorMentions] = useState<Array<{ name: string; mentions: number; responses: number; rate: number }>>([]);
  const [compMentionTotal, setCompMentionTotal] = useState(0);

  const fetchCompetitorMentions = useCallback(async () => {
    if (!tenantId || !brandId) return;
    try {
      const res = await apiGet<{ data: Array<{ name: string; mentions: number; responses: number; rate: number }>; total_responses: number }>(
        `/api/v1/tenants/${tenantId}/brands/${brandId}/visibility/competitor-mentions?limit=10`,
      );
      setCompetitorMentions(res.data);
      setCompMentionTotal(res.total_responses);
    } catch {
      // Non-critical
    }
  }, [tenantId, brandId]);

  useEffect(() => { fetchCompetitorMentions(); }, [fetchCompetitorMentions]);

  const engines = new Set(latest.map((s) => s.engine));
  const loading = visLoading || compLoading;
  const error = visError || compError;

  // Engine aggregations — derived from the windowed `history` (the same series
  // the trend chart plots) so the per-engine cards and the headline KPIs reflect
  // the windowed sample-weighted average, not a single most-recent day. Falls
  // back to `latest` when history is empty so a single-snapshot brand still
  // renders. `aggregateByEngine` sample-weights each metric across the rows.
  const engineAggs = useMemo(
    () => aggregateByEngine(history.length > 0 ? history : latest),
    [history, latest],
  );

  // Spec 220 — engines whose provider is disabled right now.
  //
  // These have no visibility rows, so `aggregateByEngine` drops them and the
  // section simply lost them: Mistral was absent for 34 days and nothing on the
  // page said why. An engine merely absent from the data (never run for this
  // brand) is NOT paused and stays absent — only a live kill switch qualifies,
  // which is why this reads the degradation payload rather than "no rows".
  const pausedEngines = useMemo(() => {
    const rows = pipelineStatus?.degradation?.engines ?? [];
    const have = new Set(engineAggs.map((a) => a.engine));
    return ENGINE_ORDER.filter(
      (e) => !have.has(e) && rows.some((r) => r.engine === e && r.failure_class === 'provider_disabled'),
    ).map((e) => {
      const row = rows.find((r) => r.engine === e)!;
      return { engine: e, since: row.last_error_at, reason: row.last_error };
    });
  }, [pipelineStatus, engineAggs]);

  // KPI values
  const kpis = useKpiValues(engineAggs, rankings, brandName);

  // SOV segments (kept for backward compat but no longer rendered)
  const sovSegments = useSovSegments(rankings);

  // Total sample size across all engines
  const totalSampleSize = useMemo(
    () => engineAggs.reduce((sum, e) => sum + e.sample_size, 0),
    [engineAggs],
  );

  // Headline reliability = worst tier across all contributing engines. Derived
  // Spec 179: the headline reliability is the tier of the POOLED window sample
  // (total responses across engines), mirroring the backend getReliabilityTier —
  // not worst-of the thin per-engine rows (which under-claimed: n=thousands read
  // "directional"). The 177 ~/amber softening still fires only when this is
  // genuinely 'directional' (a new/low-data brand). Empty → 'directional'.
  const headlineTier = useMemo<ReliabilityTier>(
    () => reliabilityTierForSampleSize(totalSampleSize),
    [totalSampleSize],
  );

  // Header subtitle
  const headerSubtitle = [brandIndustry, `baseline report`]
    .filter(Boolean)
    .join(", ");

  return (
    <div data-testid="overview-page" className="hub-page--dashboard" data-density="compact">
      {/* 1. Head region — brand identity + the page's two actions. */}
      <Region>
        <div className="hub-page__head">
          <div>
            {/* Spec 250 (review P1-13): one title rule — every title is the
                page's name. Overview's was the brand, which already lives in
                the bar's selector. The brand is the subtitle's first words, on
                its own element so gate 201 still reads exactly the name. */}
            <h1 className="hub-page__title">Overview</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
              <span className="font-medium text-[var(--hub-color-ink)]" data-testid="overview-brand-name">{brandName}</span>
              {headerSubtitle ? `, ${headerSubtitle}` : ''}
            </p>
            {/* Spec 256: the loop as navigation — this page's stage inverted, the next one click away. */}
            <div className="mt-3">
              <StageLoop current="monitor" />
            </div>
          </div>
          {/* Spec 246 (review P0-4): the actions wrap as a row, never inside a
              label. Below 720px the segmented range drops to its own row. */}
          <div className="flex flex-wrap items-center gap-3">
            {tenantId && brandId && (
              <BrandReportButton
                tenantId={tenantId}
                brandId={brandId}
                brandName={brandName}
              />
            )}
            <DateRangeSelector value={dateRange} onChange={onDateRangeChange} />
          </div>
        </div>

        {/* Pipeline Status Bar — collapsible single-line summary */}
        {pipelineStatus && (
          <div className="mt-4">
            <PipelineSummaryBar
              status={pipelineStatus}
              onRecomputeScores={handleRecomputeScores}
              onRedetectGaps={handleRedetectGaps}
              onRecomputeDomainScores={handleRecomputeDomainScores}
            />
          </div>
        )}

        {/* Spec 256: the engine strip — five engines, their status and last
            run, always shown. It absorbed the degradation banner (spec 134):
            a failing or switched-off engine's cell names its fix. */}
        <div className="mt-4">
          <EngineStrip
            degradation={pipelineStatus?.degradation}
            lastByEngine={pipelineStatus?.queries.last_by_engine}
            loading={!pipelineStatus}
            unavailable={!pipelineStatus && pipelineFailed}
            onRetry={fetchPipelineStatus}
          />
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {loading && (
          <div className="mt-8 flex justify-center">
            <Spinner className="text-violet-600" />
          </div>
        )}
      </Region>

      {!loading && (
        <>
          {/* 2. The stat row — one instrument, four cells that touch. */}
          <Region>
            {/* Spec 248 (review P1-5) — ONE anatomy for all four cells: icon
                disc, label, note, gauge, figure. It had four: a green pill under
                Quality, a gauge on Mention rate, a green figure on Sentiment,
                nothing on Share of voice. Figures are ink; tone lives in the note
                as words. Shares and scores get gauges; the signed value doesn't. */}
            <StatRow>
              <Stat
                icon="bx-star"
                label="Quality score"
                note="Weighted mention rate"
                gaugeFraction={Math.min(1, Math.max(0, Number.parseFloat(kpis.qualityScore) / 10))}
                value={`${kpis.qualityScore}/10`}
                data-testid="stat-quality"
              />
              {(() => {
                // Spec 177's honesty treatment, carried rather than dropped: a
                // directional headline keeps its "~" (notation for
                // "approximately", not colour) and states its tier and n. What
                // moves is the TONE — from an amber figure and a pill into the
                // note, in words — which is Kridt's rule and the review's P1-5.
                const headlinePct = kpis.unpromptedMentionRatePct ?? kpis.mentionRatePct;
                const isDirectional = headlineTier === 'directional';
                const tierWord =
                  headlineTier === 'directional' ? 'Directional'
                  : headlineTier === 'high_confidence' ? 'High confidence'
                  : 'Operational';
                const basis =
                  kpis.unpromptedMentionRatePct != null
                    ? `unprompted, raw ${kpis.mentionRatePct}%`
                    : 'across engines';
                return (
                  <Stat
                    icon="bx-message-rounded-check"
                    label="Mention rate"
                    note={`${tierWord}, n = ${totalSampleSize}; ${basis}`}
                    gaugeFraction={headlinePct / 100}
                    value={isDirectional ? `~${headlinePct}%` : `${headlinePct}%`}
                    data-testid="stat-mention-rate"
                    data-reliability={headlineTier}
                  />
                );
              })()}
              {/* Signed (-1..+1). Spec 251 (owner): a diverging gauge that
                  fills from its centre, with −1 and +1 at its ends — the
                  review's "centred bar" option. The note still states the
                  scale rather than a verdict: "positive" or "negative" would
                  need thresholds nobody has defined. */}
              <Stat
                icon="bx-smile"
                label="Sentiment"
                note="On a scale of −1 to +1"
                gaugeFraction={(Math.min(1, Math.max(-1, kpis.avgSentiment)) + 1) / 2}
                diverging
                scaleEnds={['−1', '+1']}
                value={`${kpis.avgSentiment >= 0 ? '+' : ''}${kpis.avgSentiment.toFixed(2)}`}
                data-testid="stat-sentiment"
              />
              <Stat
                icon="bx-pie-chart-alt-2"
                label="Share of voice"
                // Every cell carries a note (Kridt). sovSubtitle exists only when
                // a competitor is tracked; without one, the honest note is that
                // there is nothing to compare against — gate 248.A.1 found the
                // cell otherwise saying nothing.
                note={kpis.sovSubtitle ?? 'No competitors to compare against yet'}
                gaugeFraction={Math.min(1, Math.max(0, kpis.brandSov))}
                value={`${Math.round(kpis.brandSov * 100)}%`}
                data-testid="stat-share-of-voice"
              />
            </StatRow>
          </Region>

          {/* 3. Split — visibility trend beside what needs attention. */}
          <div className="hub-page__split">
            <Region>
              <RegionHead
                title="Visibility trend"
                caption="Unprompted mention rate over time"
                action={
                  competitiveHistory.length > 1 ? (
                    // Spec 249 (review P1-11): a parameter of this chart, so
                    // Kridt's Segmented control rather than a violet toggle.
                    <Segmented
                      options={CHART_MODES}
                      value={chartMode}
                      onChange={setChartMode}
                      label="Chart by"
                      testId="chart-mode-toggle"
                    />
                  ) : undefined
                }
              />
              {chartMode === 'brand' && competitiveHistory.length > 1 ? (
                <MultiBrandVisibilityChart series={competitiveHistory} />
              ) : (
                <VisibilityChart history={history} engines={engines} />
              )}
            </Region>
            <Region>
              <RegionHead
                title="Detected gaps"
                action={
                  /* Spec 088c-1: registry-backed badge next to the section header.
                     GapScore is a 0-100 severity measure; for the Overview page
                     we surface gap *count* clamped to 100.
                     Spec 248 (review P1-8): with NO gaps it rendered an amber
                     "⚠ —" — a warning for a state where nothing is wrong. The
                     badge now appears only when there is something to report. */
                  gapCount > 0 ? (
                    <ScoreBadge scoreType="GapScore" value={Math.min(gapCount, 100)} size="sm" />
                  ) : undefined
                }
              />
              <GapSummary tenantId={tenantId} brandId={brandId} headlineTier={headlineTier} />
            </Region>
          </div>

          {/* 4. Visibility — split by region when ≥2 regions (spec 171), else the
              existing flat per-engine cards (single-region brands unchanged). */}
          {hasMultiRegion ? (
            <>
              <Region>
                <RegionVisibilitySplit
                  regions={regions}
                  freshness={
                    pipelineStatus
                      ? {
                          lastComputedAt: pipelineStatus.visibility.last_computed_at,
                          isStale: pipelineStatus.visibility.is_stale,
                        }
                      : undefined
                  }
                />
              </Region>
              <Region>
                <RegionSourceOverlap overlap={regionOverlap} />
              </Region>
            </>
          ) : (
            (engineAggs.length > 0 || pausedEngines.length > 0) && (
              <Region>
                <RegionHead title="Visibility by engine" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                  {ENGINE_ORDER.map((engine) => {
                    const agg = engineAggs.find((a) => a.engine === engine);
                    if (agg) return <EngineVisibilityCard key={engine} data={agg} />;
                    const paused = pausedEngines.find((p) => p.engine === engine);
                    if (paused) {
                      return (
                        <PausedEngineCard
                          key={engine}
                          engine={paused.engine}
                          since={paused.since}
                          reason={paused.reason}
                        />
                      );
                    }
                    return null;
                  })}
                  {/* Any engine outside the canonical order still renders. */}
                  {engineAggs
                    .filter((a) => !ENGINE_ORDER.includes(a.engine))
                    .map((agg) => (
                      <EngineVisibilityCard key={agg.engine} data={agg} />
                    ))}
                </div>
              </Region>
            )
          )}

          {/* 4.5 Google AI surfaces (Search Console) — spec 089b */}
          <Region data-testid="search-console-section">
            <SearchConsolePanel brandId={brandId} tenantId={tenantId} />
          </Region>

          {/* 5. Competitor Mentions (replaces Share of Voice) */}
          {competitorMentions.length > 0 && (
            <Region>
              <RegionHead
                title="Competitor mentions — all engines combined"
                caption={`How often competitors are mentioned in AI responses about ${brandName}. Based on ${compMentionTotal} analyzed responses.`}
              />
              {/* Spec 234 §5: a light column for the range, an ink dash at
                  the value. Rows are sorted by rate desc (index 0 is the
                  most-mentioned competitor), so that top row is the one
                  mark that carries the point — its dash takes the action
                  colour and its column lifts a step. */}
              <div className="space-y-2">
                {competitorMentions.map((comp, idx) => {
                  const maxRate = competitorMentions[0]?.rate ?? 1;
                  return (
                    <div key={comp.name} className="flex items-center gap-3">
                      <span className="w-40 flex-shrink-0 truncate text-sm text-slate-700" title={comp.name}>{comp.name}</span>
                      <MeasureBar
                        fraction={maxRate > 0 ? comp.rate / maxRate : 0}
                        emphasize={idx === 0}
                        className="flex-1"
                      />
                      <span className="w-16 flex-shrink-0 text-right text-sm font-medium text-slate-700">{comp.rate}%</span>
                      <span className="w-24 flex-shrink-0 text-right text-xs text-slate-400">in {comp.responses} resp.</span>
                    </div>
                  );
                })}
              </div>
            </Region>
          )}

          {/* 6. Competitive Rankings */}
          <Region>
            <RegionHead title="Competitive rankings" />
            <RankingsTable rankings={rankings} engines={engines} />
          </Region>

          {/* 7. Narrative Summary */}
          {engineAggs.length > 0 && (
            <Region data-testid="narrative-summary">
              <div className="flex items-start gap-3">
                <span className="hub-disc">
                  <Icon name="bx-bulb" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-[var(--hub-color-ink)]">AI visibility summary</h3>
                  <p className="mt-1 text-sm leading-relaxed text-[var(--hub-color-ink-muted)]">
                    {generateNarrativeSummary(brandName, engineAggs, kpis, gapCount, headlineTier, totalSampleSize)}
                  </p>
                </div>
              </div>
            </Region>
          )}

          {/* 8. Recent Responses */}
          <Region data-testid="recent-responses">
            <RegionHead title="Recent responses" />
            {recentResponses.length === 0 ? (
              <div className="flex flex-col items-center hub-plate--flat py-8">
                <Icon name="bx-message" className="text-gray-300" />
                <p className="mt-2 text-sm text-gray-400">No responses captured yet</p>
                <Link to="/prompts" className="mt-1 text-xs text-violet-600 hover:underline">Configure prompts to start monitoring</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 border-t border-[var(--hub-color-line)] sm:grid-cols-2 lg:grid-cols-3">
                {recentResponses.map((resp) => (
                  <Link key={resp.id} to={`/responses/${resp.id}`}
                    className="border-b border-r border-[var(--hub-color-line)] p-4 transition-colors hover:bg-[var(--hub-color-surface-sunken)]"
                    data-testid="recent-response-card">
                    <div className="flex items-center justify-between">
                      <EngineBadge engine={resp.engine} />
                      <time className="text-xs text-gray-500">{formatRelativeTime(resp.captured_at)}</time>
                    </div>
                    {/* Two lines of prompt, plus the text on the element so a clipped
                        prompt can be hovered. The card is a third of the width at `lg`,
                        so a single truncated line clipped even short prompts with nothing
                        to reveal. Spec 213: `prompt_snippet` is no longer capped
                        server-side, so this tooltip now shows the COMPLETE prompt rather
                        than a 100-char prefix. Do not reinstate a server cap to 'save
                        bytes' — it makes the tooltip lie, which is the defect a user
                        reported on the Execution Matrix. No tooltip when there is no prompt. */}
                    <p
                      className="mt-2 line-clamp-2 text-sm font-medium text-gray-900"
                      title={resp.prompt_snippet || undefined}
                    >{resp.prompt_snippet || 'No prompt'}</p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      {resp.brand_mentioned === true && <span className="flex items-center gap-0.5 text-green-600"><Icon name="bx-check" />Mentioned</span>}
                      {resp.brand_mentioned === false && <span className="flex items-center gap-0.5 text-red-500"><Icon name="bx-x" />Not mentioned</span>}
                      {resp.brand_mentioned === null && <span className="flex items-center gap-0.5 text-gray-400"><Icon name="bx-minus" />Pending</span>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Region>
        </>
      )}
    </div>
  );
}
